<script lang="ts" setup>
import { ofetch } from 'ofetch'
import { flattenEntity } from 'strapi-flatten-graphql'
import type { StrapiResp } from '~/models/Strapi'

interface Props {
  keyword: string
}

const props = defineProps<Props>()
const emit = defineEmits(['setMeta', 'setCanon'])
const { $config } = useNuxtApp()
const route = useRoute()
const articles = ref()
const authors = ref()
const popularList = ref()
const strapiConfig = useStrapiAPIConfig()

if (!isEmpty(props.keyword)) {
  getStrapiArticle(props.keyword)
  getPopularSearch(props.keyword)
  getAuthor()
}

watch(() => props.keyword, (newVal) => {
  if (!isEmpty(newVal)) {
    getStrapiArticle(newVal)
    getPopularSearch(newVal)
    getAuthor()
  }
})

async function getStrapiArticle(keyword: string) {
  try {
    const [articleData] = await Promise.all([
      useAsyncData('get_articles', () => strapiConfig(`/articles?filters[title][$containsi]=${useTextToParam(keyword, true)}&filters[summary][$containsi]=${useTextToParam(keyword, true)}&sort=createdAt:desc&pagination[page]=0&pagination[pageSize]=4&populate=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`), { server: true }),
    ])
    const rawArticle = articleData.data.value
    if (!isEmpty(rawArticle?.data)) {
      articles.value = rawArticle.data.map((item: any) => {
        const article = flattenEntity(item)
        const thumbnail = article.thumbnail?.data ? flattenEntity(article.thumbnail?.data) : null
        const topics = article.topics?.data.map((topic: any) => flattenEntity(topic))
        const createdBy = article.createdBy?.data ? flattenEntity(article.createdBy?.data) : null
        const fileAttachment = article.file_attachment?.data ? flattenEntity(article.file_attachment?.data) : null
        return { ...article, thumbnail, topics, createdBy, file_attachment: fileAttachment }
      })
    }
  }
  catch (error) {
    console.error('Error fetching data0', error)
    showError({ message: 'Failed to load data0', statusCode: 500 })
  }
}

async function getAuthor() {
  try {
    const [authorsData] = await Promise.all([
      useAsyncData('get_authors', () => strapiConfig(`/authors?populate=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`), { server: true }),
    ])
    const rawAuthors = authorsData.data.value
    if (!isEmpty(rawAuthors?.data)) {
      authors.value = rawAuthors?.data.map((item: any) => {
        const author = flattenEntity(item)
        const thumbnail = !isEmpty(author.thumbnail) ? author.thumbnail?.data.map((topic: any) => flattenEntity(topic))[0] : []
        return { ...author, thumbnail }
      })
    }
  }
  catch (error) {
    console.error('Error fetching data1', error)
    showError({ message: 'Failed to load data1', statusCode: 500 })
  }
}

function selectAuthor(username: string) {
  return !isEmpty(authors.value) ? authors.value.find((author: any) => author.username === username) : {}
}

async function getPopularSearch(keyword: string) {
  try {
    const [popularData] = await Promise.all([
      useAsyncData('get_popular_key', () => ofetch(`${$config.public.SEARCHAPI_BASE_URL}/jobs/suggested-titles`, {
        method: 'POST',
        body: { job_title: keyword },
      })),
    ])

    const rawPopular = popularData.data.value
    popularList.value = !isEmpty(rawPopular) ? rawPopular.slice(0, 4) : []
  }
  catch (error) {
    console.error('Error fetching data 2', error)
    showError({ message: 'Failed to load data 2', statusCode: 500 })
  }
}

watch(() => route.query, async (newVal, oldVal) => {
  // always update meta based on route
  if (newVal.st) {
    if (!isEmpty(newVal?.q)) {
      getStrapiArticle(newVal?.q.toString())
      getPopularSearch(newVal?.q.toString())
    }
  }
}, { deep: true, immediate: false })
</script>

<template>
  <div :class="`${!isEmpty(popularList) || !isEmpty(articles) ? 'pb-60' : ''} bg-gray-50 mt-20 pt-10`">
    <div v-if="!isEmpty(popularList)" class="text-xs bg-white max-w-4xl mx-auto p-4 md:p-6 rounded-md">
      <p class="mb-4">
        Popular Search
      </p>
      <div class="grid sm:grid-cols-2 gap-2 md:gap-6">
        <NuxtLink v-for="(item, idx) in popularList" :key="idx" :href="useGenerateSearchUrl(item)" class="py-2 px-4 rounded-full bg-gray-50 flex items-center text-[#5843E5]">
          {{ item }}
          <Icon name="heroicons:arrow-up-right-solid" class="w-4 h-4 ml-auto" />
        </NuxtLink>
      </div>
    </div>
    <div v-if="!isEmpty(articles)" class="mt-20 max-w-4xl mx-auto">
      <p class="text-4xl font-bold mb-6 text-center">
        Related Articles
      </p>
      <div class="grid md:grid-cols-2 gap-6">
        <CareerGuideArticleCard v-for="(item, idx) in articles" :key="idx" :compact-mode="false" :is-loading="false" :article="item" :author="selectAuthor(item?.createdBy?.username)" />
      </div>
    </div>
  </div>
</template>
